import { useFetchStudioAvailability } from '@/api/hooks/useFetchStudioAvailability'
import { colors } from '@/constants/colors'
import { Button } from '@/ui/core/button'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { View } from 'react-native'
import Svg, { Path } from 'react-native-svg'

type StudioAvailabilityCarouselProps = {
	appointmentId: number
}

const CarouselLeftArrow = ({ size = 16, fill = colors.inactive_1 }) => (
	<Svg width={size} height={size} viewBox="0 0 37 16" fill="none">
		<Path
			d="M0.292893 8.7071C-0.0976311 8.31658 -0.097631 7.68341 0.292893 7.29289L6.65685 0.92893C7.04738 0.538405 7.68054 0.538405 8.07107 0.92893C8.46159 1.31945 8.46159 1.95262 8.07107 2.34314L2.41421 8L8.07107 13.6569C8.46159 14.0474 8.46159 14.6805 8.07107 15.0711C7.68054 15.4616 7.04738 15.4616 6.65685 15.0711L0.292893 8.7071ZM37 9L1 9L1 7L37 7L37 9Z"
			fill={fill}
		/>
	</Svg>
)

const CarouselRightArrow = ({ size = 16, fill = colors.inactive_1 }) => (
	<Svg width={size} height={size} viewBox="0 0 37 16" fill="none">
		<Path
			d="M36.7071 8.44636C37.0976 8.05584 37.0976 7.42267 36.7071 7.03215L30.3431 0.668187C29.9526 0.277663 29.3195 0.277663 28.9289 0.668187C28.5384 1.05871 28.5384 1.69188 28.9289 2.0824L34.5858 7.73925L28.9289 13.3961C28.5384 13.7866 28.5384 14.4198 28.9289 14.8103C29.3195 15.2008 29.9526 15.2008 30.3431 14.8103L36.7071 8.44636ZM8.74228e-08 8.73926L36 8.73925L36 6.73925L-8.74228e-08 6.73926L8.74228e-08 8.73926Z"
			fill={fill}
		/>
	</Svg>
)

const StudioAvailabilityCarousel = ({
	appointmentId,
}: StudioAvailabilityCarouselProps) => {
	const [currentIndex, setCurrentIndex] = useState(0)
	const {
		studioAvailability,
		isLoading: isLoadingStudioAvailability,
		error,
	} = useFetchStudioAvailability({ appointmentId })

	if (error || (!isLoadingStudioAvailability && !studioAvailability?.length)) {
		return null
	}

	const nextAppointment = () => {
		if (studioAvailability && currentIndex < studioAvailability.length - 1) {
			setCurrentIndex((prev) => prev + 1)
		}
	}

	const prevAppointment = () => {
		if (currentIndex > 0) {
			setCurrentIndex((prev) => prev - 1)
		}
	}

	const currentAppointment = studioAvailability?.[currentIndex]

	return (
		<VStack className="justify-center items-center w-[256px] bg-white mb-2" gap={16}>
			{studioAvailability && currentAppointment && (
				<VStack className="gap-4 items-center justify-center pt-5 w-full">
					<Text className="font-primary-bold text-xs text-center tracking-[.15em] text-palette_6">
						NEXT AVAILABLE
					</Text>

					<VStack className="gap-1 items-center justify-center pt-4 pb-1.5 w-full min-h-16 border-t border-palette_3">
						<VStack className="gap-1 items-center">
							<Text className="font-primary text-md text-center text-palette_6">
								{currentAppointment.location}
							</Text>
							<Text className="font-primary-demi text-lg text-center text-palette_6">
								{currentAppointment.date}
							</Text>
						</VStack>

						{studioAvailability.length > 1 && (
							<HStack className="items-center justify-center w-full gap-3">
								<Button
									variant="ghostBorderless"
									size="sm"
									onPress={prevAppointment}
									disabled={currentIndex === 0}
									className="p-2 !border-0"
								>
									<CarouselLeftArrow
										size={16}
										fill={currentIndex === 0 ? colors.palette_3 : colors.palette_6}
									/>
								</Button>

								<HStack className="gap-1">
									{studioAvailability.map((_, index) => (
										<View
											// biome-ignore lint/suspicious/noArrayIndexKey: stable index usage
											key={`availability-dot-${index}`}
											className={`w-2 h-2 rounded-full ${
												index === currentIndex ? 'bg-palette_6' : 'bg-palette_3'
											}`}
										/>
									))}
								</HStack>

								<Button
									variant="ghostBorderless"
									size="sm"
									onPress={nextAppointment}
									disabled={currentIndex === studioAvailability.length - 1}
									className="p-2 !border-0"
								>
									<CarouselRightArrow
										size={16}
										fill={
											currentIndex === studioAvailability.length - 1
												? colors.palette_3
												: colors.palette_6
										}
									/>
								</Button>
							</HStack>
						)}
					</VStack>
				</VStack>
			)}
		</VStack>
	)
}

export { StudioAvailabilityCarousel }
